// eslint-disable-next-line no-unused-vars
import React, { memo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import useRouter from 'Hooks/useRouter';
import MFAModalComponent from 'Components/MFA/MFAModal';
import useAnalytics from 'Hooks/useAnalytics';
import { API_MFA_ENABLE, API_MFA_QRCODE_CONTENT } from 'Constants';

const languages = [
  { label: 'RO', value: 'ro' },
  { label: 'EN', value: 'en' }
];

const steps = ['intro', 'verify', 'success'];

const defaultValues = {
  authCode: '',
};

const MFAModal = memo(() => {
  const { data: user, userLogout, setUserData, mutate } = useUser();
  const { t } = useTranslation();
  const fetcher = useFetch();
  const { push } = useRouter();
  const analytics = useAnalytics();
  const [step, setStep] = useState(steps[0]);
  const [mfaKey, setMfaKey] = useState(null);
  const [loadingMFAKey, setLoadingMFAKey] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const {
    formState: { errors: rhfErrors },
    register,
    getValues,
    trigger,
    watch,
  } = useForm({
    defaultValues,
    mode: 'onChange',
  });

  const authCodeValue = watch('authCode');
  const required = t('errors.fieldBlank');
  const minLength = value => ({ value, message: t('errors.fieldMinLength', { minLength: value }) });
  const maxLength = value => ({ value, message: t('errors.fieldMaxLength', { maxLength: value }) });

  const validators = {
    authCode: {
      required,
      minLength: minLength(6),
      maxLength: maxLength(6),
    }
  };

  const getMFAKey = () => {
    setLoadingMFAKey(true);
    fetcher(API_MFA_QRCODE_CONTENT)
      .then(res => {
        setMfaKey(res);
      }).finally(() => setLoadingMFAKey(false));
  };

  useEffect(() => {
    if (step === 'verify' && !mfaKey) {
      getMFAKey();
    }
  }, [step]);

  const setUserAsLoggedIn = async () => {
    setUserData({ isTwoFactorAuthenticationVerified: true, loggedIn: true });
    await mutate();
  };

  const handleSubmit = async () => {
    analytics.track('send_generated_auth_code');
    await trigger();

    const rhfErrorsKeys = Object.keys(rhfErrors);
    if (rhfErrorsKeys.length || authCodeValue === '') {
      return;
    }

    const {
      authCode,
    } = getValues();

    setErrors([]);
    setSubmitLoading(true);
    fetcher(API_MFA_ENABLE, {
      method: 'POST',
      body: JSON.stringify({ code: authCode }),
    }).then(() => {
      setStep(steps[2]);
      setUserAsLoggedIn();
    }).catch(async err => {
      if (err.info?.hasTwoFactorAuthenticationEnabled) {
        setUserAsLoggedIn();
        handleRedirect();
      }
      setErrors([{ message: err.info.error, type: 'error' }]);
    })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const handleLogout = event => {
    analytics.track('user_logout');
    userLogout(event);
  };

  const handleRedirect = async () => {
    analytics.track('redirect_to_dashboard');
    push('/');
  };

  return (
    <MFAModalComponent
      step={ step }
      setStep={ setStep }
      languages={ languages }
      language={ languages.find(lang => lang.value === user?.language) }
      setLanguage={ lang => setUserData({ language: lang.value }) }
      onLogout={ handleLogout }
      mfaKey={ mfaKey }
      loadingMFAKey={ loadingMFAKey }
      submitLoading={ submitLoading }
      onSubmit={ handleSubmit }
      rhfErrors={ rhfErrors }
      register={ register }
      validators={ validators }
      errors={ errors }
      onRedirect={ handleRedirect }
    />
  );
});

MFAModal.displayName = 'MFAModal';

export default MFAModal;
